import { gql } from '@apollo/client';
import { LoadingIcon } from '@assets/icons';
import { InfiniteScrollGridComponents, useGraphqlPagination } from '@components/infiniteScroll';
import { Typography } from '@components/typography';
import {
  GetLandingPageRecentWorkspacesQuery,
  Organization,
  Workspace,
  useGetLandingPageRecentWorkspacesQuery,
} from '@generated/graphql';
import { Box, Stack } from '@mui/material';
import { useCallback, useEffect } from 'react';
import { VirtuosoGrid } from 'react-virtuoso';
import { LandingPageHeader } from './LandingPageHeader';
import { LandingPageWorkspaceCard } from './LandingPageWorkspaceCard';
import { getOrganizationExpirationData } from './utils';

gql`
  query GetLandingPageRecentWorkspaces($cursor: String, $limit: Int) {
    getWorkspaces(cursor: $cursor, limit: $limit) {
      name
      organization
      organizationId
      previewThumbnail
      status
      updatedAt
      workspaceId
    }
  }
`;

export const LandingPageRecentWorkspaces = ({
  organizations,
}: {
  organizations: Organization[];
}) => {
  const {
    data: workspaces,
    hasMore,
    loading: workspacesLoading,
    fetchMore: fetchMoreWorkspaces,
  } = useGraphqlPagination<GetLandingPageRecentWorkspacesQuery, Workspace>(
    useGetLandingPageRecentWorkspacesQuery({
      variables: {
        limit: 30,
      },
      fetchPolicy: 'network-only',
    }),
    'getWorkspaces',
    'workspaceId',
  );
  useEffect(() => {
    if (!workspacesLoading && workspaces.length < 30) {
      void fetchMoreWorkspaces();
    }
  }, [workspacesLoading, workspaces, fetchMoreWorkspaces]);

  const item = useCallback(
    (_, workspace: Partial<Workspace>) => {
      const { expired } =
        getOrganizationExpirationData(organizations, workspace.organizationId) || {};
      return (
        <LandingPageWorkspaceCard
          key={workspace?.workspaceId}
          expired={expired}
          organizationName={workspace?.organization}
          {...workspace}
        />
      );
    },
    [organizations],
  );

  return (
    <Stack sx={{ height: '100%', overflowY: 'hidden' }}>
      <LandingPageHeader title="Recent workspaces" />
      {workspaces?.length > 0 ? (
        <VirtuosoGrid
          totalCount={workspaces?.length}
          endReached={fetchMoreWorkspaces}
          components={InfiniteScrollGridComponents}
          data={[...workspaces, ...(hasMore ? [{ workspaceId: 'loading' }] : [])]}
          itemContent={item}
        />
      ) : (
        <Box display="flex" height="90%" alignItems="center" justifyContent="center">
          {workspacesLoading ? (
            <LoadingIcon size={16} />
          ) : workspaces.length === 0 ? (
            <Typography>Your organization has no workspaces.</Typography>
          ) : (
            <Typography>No workspaces found that match your search.</Typography>
          )}
        </Box>
      )}
    </Stack>
  );
};
